import {produce} from 'immer'
import type {StateCreator} from 'zustand'
import type {Comment, DiffAnnotation, Thread} from '@github-ui/diff-lines/conversation-types'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'

export type MarkersByFile = {
  [key: LineKey | FileKey]: {annotations: DiffAnnotation[]; threads: Thread[]}
}

export type MarkersInfo = {
  [filePath: string]: MarkersByFile
}

export type Markers = {
  threads: {[id: number]: Thread}
  annotations: {[id: number]: DiffAnnotation}
}

export type MarkersSliceOpts = {
  markers?: MarkersInfo | undefined
}

type DeleteThreadCommentResult = 'not_found' | 'comment_deleted' | 'thread_deleted'

export type MarkersSlice = {
  markers?: MarkersInfo

  getThreadFromCommentID: (commentID: number) => Thread | undefined
  getAnnotationFromID: (annotationID: number) => DiffAnnotation | undefined
  getThread: (threadID: number, filePath: string, lineKey: LineKey | FileKey) => Thread | undefined

  markersActions: {
    updateThread: (
      id: number,
      filePath: string,
      lineKey: LineKey | FileKey,
      transform: (old: Thread | undefined) => Thread | undefined,
    ) => void
    updateThreads: (transform: (old: Thread[]) => Thread[]) => void
    deleteThreadComment: (
      threadID: number,
      commentID: string,
      filePath: string,
      lineKey: LineKey | FileKey,
    ) => DeleteThreadCommentResult
    updateThreadComment: (args: {
      threadID: number
      filePath: string
      lineMarkersKey: LineKey | FileKey
      match: (storedComment: Comment) => boolean
      transform: (old: Comment | undefined) => Comment | undefined
    }) => void
  }
}

export const createMarkersSlice =
  ({markers}: MarkersSliceOpts): StateCreator<MarkersSlice, [['zustand/devtools', never]], [], MarkersSlice> =>
  (set, get) => ({
    markers,

    getThread: (threadID, filePath, lineMarkersKey) => {
      const currentMarkers = get().markers
      if (!currentMarkers) {
        return undefined
      }
      return currentMarkers[filePath]?.[lineMarkersKey]?.threads?.find(thread => Number(thread.id) === threadID)
    },
    getThreadFromCommentID: commentID => {
      const currentMarkers = get().markers
      if (!currentMarkers) {
        return undefined
      }
      const threads = Object.values(currentMarkers).flatMap(files =>
        Object.values(files).flatMap(threadsInLine => {
          return threadsInLine?.threads ?? []
        }),
      )
      if (!threads) return undefined

      for (const thread of Object.values(threads)) {
        if (thread?.commentsData?.comments.some(c => c.databaseId === commentID)) {
          return thread
        }
      }
      return undefined
    },
    getAnnotationFromID: annotationID => {
      const currentMarkers = get().markers
      if (!currentMarkers) {
        return undefined
      }
      const annotations = Object.values(currentMarkers).flatMap(files =>
        Object.values(files).flatMap(annotationsInLine => {
          return annotationsInLine?.annotations ?? []
        }),
      )

      if (!annotations) return undefined

      for (const annotation of Object.values(annotations)) {
        if (annotation.databaseId === annotationID) {
          return annotation
        }
      }
    },

    markersActions: {
      updateThread: (id, filePath, lineMarkersKey, transform) => {
        const oldMarkers = get().markers
        if (!oldMarkers) {
          return
        }
        const oldThread = oldMarkers[filePath]?.[lineMarkersKey]?.threads?.find(thread => Number(thread.id) === id)

        const newThread = transform(oldThread)
        if (!newThread) {
          return
        }

        set(
          {
            markers: produce(oldMarkers, m => {
              const modifiedThreadIndex = m[filePath]?.[lineMarkersKey]?.threads?.findIndex(
                thread => Number(thread.id) === id,
              )
              if (modifiedThreadIndex === undefined || modifiedThreadIndex === -1) {
                if (!m[filePath]) {
                  m[filePath] = {}
                }
                if (!m[filePath][lineMarkersKey]) {
                  m[filePath][lineMarkersKey] = {threads: [], annotations: []}
                }
                m[filePath][lineMarkersKey].threads.push(newThread)
              } else if (m[filePath] && m[filePath][lineMarkersKey] && modifiedThreadIndex !== -1) {
                const previousPositioning =
                  newThread.positioning ?? m[filePath][lineMarkersKey].threads[modifiedThreadIndex]?.positioning
                const previousSubject =
                  newThread.subject ?? m[filePath][lineMarkersKey].threads[modifiedThreadIndex]?.subject
                const threadToSet = {...newThread, positioning: previousPositioning, subject: previousSubject}
                m[filePath][lineMarkersKey].threads[modifiedThreadIndex] = threadToSet
              }
            }),
          },
          undefined,
          'updateThread',
        )
      },
      updateThreads: transform => {
        const oldMarkers = get().markers
        if (!oldMarkers) {
          return
        }

        set({
          markers: produce(oldMarkers, m => {
            for (const fileKey of Object.keys(oldMarkers)) {
              const fileThreads = m[fileKey]
              if (!fileThreads) continue
              for (const lineKey of Object.keys(fileThreads)) {
                const lineMarkers = fileThreads[lineKey as LineKey | FileKey]
                if (!lineMarkers) continue
                const threads = lineMarkers.threads
                if (!threads) continue
                const newThreads = transform(threads)
                lineMarkers.threads = newThreads
              }
            }
          }),
        })
      },
      deleteThreadComment: (threadID, commentID, filePath, lineMarkersKey) => {
        let result: DeleteThreadCommentResult = 'not_found'
        const oldMarkers = get().markers
        if (!oldMarkers) {
          return result
        }

        set(
          {
            markers: produce(oldMarkers, m => {
              const thread = m[filePath]?.[lineMarkersKey]?.threads?.find(
                currentThread => Number(currentThread.id) === threadID,
              )
              if (!thread) {
                result = 'not_found'
                return
              }

              const commentsData = thread.commentsData
              const filteredComments = commentsData?.comments.filter(c => c.id !== commentID) ?? []

              if (!commentsData || commentsData.comments.length === filteredComments.length) {
                result = 'not_found'
                return
              }

              if (filteredComments.length === 0) {
                result = 'thread_deleted'
                const threadsArray = m[filePath]?.[lineMarkersKey]?.threads
                if (threadsArray) {
                  const index = threadsArray.findIndex(currentThread => Number(currentThread.id) === threadID)
                  if (index !== -1) {
                    threadsArray.splice(index, 1)
                  }
                }
              } else {
                result = 'comment_deleted'
                if (commentsData) {
                  commentsData.comments = filteredComments
                }
              }
            }),
          },
          undefined,
          'deleteThreadComment',
        )

        return result
      },
      updateThreadComment: ({threadID, filePath, lineMarkersKey, match, transform}) => {
        const oldMarkers = get().markers
        if (!oldMarkers) {
          return
        }

        set(
          {
            markers: produce(oldMarkers, m => {
              const thread = m[filePath]?.[lineMarkersKey]?.threads?.find(
                currentThread => Number(currentThread.id) === threadID,
              )
              if (!thread) {
                return
              }
              const commentsData = thread.commentsData

              const index = commentsData?.comments.findIndex(match)
              let oldComment: Comment | undefined = undefined
              if (typeof index === 'number' && index !== -1) {
                oldComment = commentsData?.comments[index]
              }
              const newComment = transform(oldComment)
              if (!newComment) {
                return
              }

              if (index === -1 || typeof index !== 'number') {
                commentsData?.comments.push(newComment)
              } else {
                if (commentsData && commentsData.comments) {
                  commentsData.comments[index] = newComment
                }
              }
            }),
          },
          undefined,
          'updateThreadComment',
        )
      },
    },
  })
